import api from '../api';
import { taskPollingService } from './taskPollingService';
import { ensureCampaign, ensureHomeDecorCampaign } from './campaignHelpers';
import type { GeneratedImageRef, GenerationResponse, TaskResponse } from './types';

// Generate Image from Moodboard (Multiple Reference Images)
const generateFromMoodboard = async (
    imageFiles: File[],
    prompt: string,
    aspectRatio: string = '1024x1024',
    imageSize: string = '1K',
    // Canvas reference names aligned to imageFiles order (e.g. ["imagem1", "vestido azul"]).
    // Sent as source_image_labels (aligned 1:1 with source_image_ids) so the backend anchors
    // each reference image with "[Referência: <nome>]" and "@nome" in the prompt binds to it.
    imageLabels: string[] = [],
    // Generation profile the user picked in the canvas. Omitted when they keep
    // the default, so the module resolves its own chain exactly as before.
    setup?: string,
    // Marketing Studio › Imagens: layer_decomposition força o Seedream 5.0 Pro layerize no
    // backend (1 imagem de entrada → base + camadas PNG). sourceApp identifica a origem.
    // outputFormat: PNG/JPG do painel de saída (igual aos demais apps).
    options?: { layerDecomposition?: boolean; sourceApp?: string; outputFormat?: 'png' | 'jpeg'; templateSlug?: string }
): Promise<GenerationResponse> => {
    // 1. Ensure campaign
    const campaignId = await ensureCampaign();

    // 2. Upload all images (order preserved: imageIds[i] ↔ imageFiles[i] ↔ imageLabels[i])
    const uploadPromises = imageFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('image_file', file);
        formData.append('status', 'uploaded');
        formData.append('campaign_id', campaignId.toString());

        const response = await api.post('/images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return (response.data as any).data.id;
    });

    const imageIds = await Promise.all(uploadPromises);

    // Labels aligned 1:1 with source_image_ids (same order as imageFiles). Empty string
    // means "no label for this image" — kept in place so the array stays aligned.
    const sourceImageLabels = imageIds.map((_, i) => (imageLabels[i] ?? '').trim());

    // 3. Create Generation Task
    const response = await api.post('/images/generate-task', {
        name: 'Moodboard Generation - ' + new Date().toLocaleTimeString(),
        prompt: prompt,
        n: 1,
        size: aspectRatio,
        quality: 'high',
        draft: false,
        campaign_id: campaignId,
        source_image_ids: imageIds,
        ...(sourceImageLabels.some(l => l !== '') ? { source_image_labels: sourceImageLabels } : {}),
        image_size: imageSize,
        source_app: options?.sourceApp ?? 'moodboard',
        ...(options?.layerDecomposition ? { layer_decomposition: true } : {}),
        ...(options?.outputFormat ? { output_format: options.outputFormat } : {}),
        ...(options?.templateSlug ? { template_slug: options.templateSlug } : {}),
        ...(setup ? { setup } : {})
    });

    return response.data as GenerationResponse;
};

// Clothing Generation — supports 1 file (legacy) or N files (multi-image, QA v3 #3)
const generateClothing = async (
    clothingFilesOrFile: File[] | File,
    prompt: string,
    options: { model?: string; n?: number; size?: string; quality?: string; flow?: string } = {}
): Promise<GenerationResponse> => {
    const files = Array.isArray(clothingFilesOrFile) ? clothingFilesOrFile : [clothingFilesOrFile];

    const formData = new FormData();
    // First image: use legacy field name for backward compatibility — backends that haven't been
    // updated yet still receive a working request with image #1.
    files.forEach((file, idx) => {
        if (idx === 0) {
            formData.append('clothing_image', file);
        }
        formData.append('clothing_images[]', file);
    });
    formData.append('prompt', prompt);
    if (options.model) formData.append('model', options.model);
    if (options.n) formData.append('n', options.n.toString());
    if (options.size) formData.append('size', options.size);
    if (options.quality) formData.append('quality', options.quality);
    if (options.flow) formData.append('flow', options.flow);

    const response = await api.post('/ai/generate-clothing', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data as GenerationResponse;
};

// Text to Image Generation (Standard)
const generateImage = async (
    prompt: string,
    aspectRatio: string = '1024x1024',
    imageSize?: string,
    taskParameters?: Record<string, unknown>,
    setup?: string,
    // Transparent-background PNG request (workflow editor's Transparent Image node).
    // JPEG and transparency are mutually exclusive, so this always implies PNG.
    // 6º e ÚLTIMO: `setup` já ocupa o 5º nos call sites da main.
    outputMode?: 'transparent_png',
): Promise<GenerationResponse> => {
    // Ensure campaign exists
    const campaignId = await ensureCampaign();

    const payload: Record<string, unknown> = {
        name: 'Text to Image - ' + new Date().toLocaleTimeString(),
        prompt: prompt,
        n: 1,
        size: aspectRatio,
        quality: 'high',
        draft: false,
        campaign_id: campaignId
    };

    if (imageSize) {
        payload.image_size = imageSize;
    }

    if (outputMode) {
        payload.output_mode = outputMode;
        payload.output_format = 'png';
    }

    // Flow marker (e.g. { campaign_type: 'casting' }) so the backend can resolve
    // the right AppType / pinned model for this generation.
    if (taskParameters && Object.keys(taskParameters).length > 0) {
        payload.task_parameters = taskParameters;
    }

    if (setup) {
        payload.setup = setup;
    }

    const response = await api.post('/images/generate-task', payload);
    return response.data as GenerationResponse;
};

// Generate Look (Batch Creation)
// Now accepts either File[] (legacy) or number[] (pre-uploaded backend IDs)
const generateLook = async (
    imageFilesOrIds: File[] | number[],
    modelDescription: string,
    styleDetails: { stylePrompt?: string },
    aspectRatio: string,
    isLowerBody: boolean,
    brandbookId?: number,
    brandStyleSource?: 'specific' | 'auto' | 'none',
    lookIndex?: number,
    poseIndex?: number,
    imageSize: '1K' | '2K' | '4K' = '1K', // NEW: Resolution for Gemini
    providerOverride?: 'gemini' | 'bytedance' | 'openai', // REMOÇÃO: docs/RFC-FASHION-BATCH-PROVIDER-PROMPT-OVERRIDE.md
    promptOverride?: string,
    outputFormat: 'png' | 'jpeg' = 'png',
    customModelId?: number,
    // Generation setup ("profile") slug chosen by the user. Omitted when they
    // keep the default, so the backend resolves the chain exactly as before.
    setup?: string,
    // Optional per-image names, aligned 1:1 with imageFilesOrIds. Sent as
    // source_image_labels so the backend tags each reference image with
    // "[Referência: <name>]" in the Gemini payload — lets a Workflow prompt reference
    // "imagem 1"/"imagem 2" by name reliably.
    //
    // 16th and LAST on purpose. `setup` already occupies 15 on main, and both are
    // optional strings/arrays, so swapping them compiles fine and silently sends the
    // label array as the setup slug. Anything added later goes after this one.
    sourceImageLabels?: string[],
    // Modelo fixado pela equipe interna ao regerar (ferramenta de diagnóstico).
    // 17º e ÚLTIMO — pelo mesmo motivo documentado acima: trocar a ordem dos
    // opcionais compila e manda o valor no campo errado, em silêncio.
    modelVersion?: string,
    // Transparent-background PNG request (nó Imagem Transparente do editor de workflow).
    // JPEG e transparência são mutuamente exclusivos, então isto sempre implica PNG.
    // 18º e ÚLTIMO — mesma razão documentada acima.
    outputMode?: 'transparent_png'
): Promise<GeneratedImageRef | null> => {
    // 1. Ensure campaign
    const campaignId = await ensureCampaign();

    // 2. Get image IDs - either use pre-uploaded IDs or upload files now
    let imageIds: number[];

    if (imageFilesOrIds.length > 0 && typeof imageFilesOrIds[0] === 'number') {
        // Already have backend IDs (pre-uploaded)
        imageIds = imageFilesOrIds as number[];
    } else {
        // Legacy: upload files now (may fail with ERR_FILE_NOT_FOUND if files are stale)
        const uploadPromises = (imageFilesOrIds as File[]).map(async (file) => {
            const formData = new FormData();
            formData.append('image_file', file);
            formData.append('status', 'uploaded');
            formData.append('campaign_id', campaignId.toString());

            const response = await api.post('/images', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return (response.data as any).data.id;
        });

        imageIds = await Promise.all(uploadPromises);
    }

    // 3. Construct prompt: use override when provided, otherwise build from model + style
    const useOverride = Boolean(promptOverride?.trim());
    let basePrompt: string;
    let shootingPrompt: string;

    if (useOverride) {
        basePrompt = promptOverride!.trim();
        shootingPrompt = '';
    } else {
        // NOTE: Avoid "Model:" prefix which can trigger Gemini's child safety filters when combined with age descriptors
        basePrompt = `${modelDescription}. Wearing the uploaded clothing items.`;
        const rawShootingPrompt = styleDetails.stylePrompt || '';
        shootingPrompt = rawShootingPrompt.length > 3600 ? rawShootingPrompt.slice(0, 3600) : rawShootingPrompt;
    }

    // 4. Create Generation Task
    const body: Record<string, unknown> = {
        name: 'Look Generation - ' + new Date().toLocaleTimeString(),
        prompt: basePrompt,
        n: 1,
        size: aspectRatio,
        quality: 'high',
        draft: false,
        campaign_id: campaignId,
        source_image_ids: imageIds,
        brandbook_id: brandbookId,
        brand_style_source: brandStyleSource || 'none',
        image_size: imageSize,
        output_format: outputMode ? 'png' : outputFormat,
        task_parameters: {
            shooting_prompt: shootingPrompt,
            look_index: lookIndex,
            pose_index: poseIndex
        }
    };
    if (providerOverride) body.provider = providerOverride;
    if (useOverride) (body.task_parameters as Record<string, unknown>).prompt_override = basePrompt;
    if (customModelId) body.custom_model_id = customModelId;
    if (setup) body.setup = setup;
    if (modelVersion) body.model_version = modelVersion;
    // Only send labels when at least one image is actually named, and align them to the
    // ids/paths order sent above so paths[i] ↔ label[i] on the backend.
    if (sourceImageLabels?.some((l) => l && l.trim())) body.source_image_labels = sourceImageLabels;
    if (outputMode) body.output_mode = outputMode;

    const response = await api.post('/images/generate-task', body);

    // pollTask com teto limitado (~8min: maxAttempts=62, backoff 2s→4s→8s).
    // Cobre com folga o fallback lento do ByteDance (~6,5min no p95), mas fica bem
    // abaixo dos ~16min que as 120 tentativas padrão deixariam uma task travada
    // segurar o lote sequencial. Mantido pollTask (não listenToTask) para preservar o
    // tratamento de janela de manutenção e não depender do WebSocket/Echo na geração.
    const completedTask = await taskPollingService.pollTask((response.data as GenerationResponse).taskId, 2000, 62);

    if (completedTask.images && completedTask.images.length > 0) {
        return {
            id: completedTask.images[0].id,
            url: completedTask.images[0].url,
            // Modelo efetivo + task: já vinham na resposta do polling e eram
            // descartados. Alimentam a tag de atribuição interna.
            model: completedTask.images[0].metadata?.model ?? null,
            taskId: completedTask.id
        };
    }
    return null;
};

// Generate New Pose
const generateNewPose = async (
    baseImageId: string | number,
    stylePrompt: string,
    aspectRatio: string,
    brandbookId?: number,
    brandStyleSource?: 'specific' | 'auto' | 'none',
    modelDescription?: string,
    lookIndex?: number,
    poseIndex?: number,
    imageSize: '1K' | '2K' | '4K' = '1K', // NEW: Resolution for Gemini
    providerOverride?: 'gemini' | 'bytedance' | 'openai', // REMOÇÃO: docs/RFC-FASHION-BATCH-PROVIDER-PROMPT-OVERRIDE.md
    promptOverride?: string,
    outputFormat: 'png' | 'jpeg' = 'png',
    // Same profile the base image used: every pose of a look must follow the
    // profile the user chose, otherwise poses 2+ silently fall back to the
    // default model and the look ends up mixing models.
    setup?: string,
    // Modelo fixado pela equipe interna ao regerar. Último parâmetro,
    // como no generateLook, pelo risco de ordem posicional.
    modelVersion?: string
): Promise<GeneratedImageRef | null> => {
    const campaignId = await ensureCampaign();

    const useOverride = Boolean(promptOverride?.trim());
    let basePrompt: string;
    let shootingPrompt2: string;

    if (useOverride) {
        basePrompt = promptOverride!.trim();
        shootingPrompt2 = '';
    } else {
        // NOTE: Avoid "Model:" prefix and "pose variation" terms which trigger Gemini's child safety filters
        basePrompt = modelDescription || '';
        const rawShootingPrompt2 = stylePrompt || '';
        shootingPrompt2 = rawShootingPrompt2.length > 3600 ? rawShootingPrompt2.slice(0, 3600) : rawShootingPrompt2;
    }

    const body: Record<string, unknown> = {
        name: 'New Pose Generation - ' + new Date().toLocaleTimeString(),
        prompt: basePrompt,
        n: 1,
        size: aspectRatio,
        campaign_id: campaignId,
        source_image_ids: [typeof baseImageId === 'string' ? parseInt(baseImageId) : baseImageId],
        brandbook_id: brandbookId,
        brand_style_source: brandStyleSource || 'none',
        image_size: imageSize,
        output_format: outputFormat,
        task_parameters: {
            shooting_prompt: shootingPrompt2,
            look_index: lookIndex,
            pose_index: poseIndex
        }
    };
    if (providerOverride) body.provider = providerOverride;
    if (useOverride) (body.task_parameters as Record<string, unknown>).prompt_override = basePrompt;
    if (setup) body.setup = setup;
    if (modelVersion) body.model_version = modelVersion;

    const response = await api.post('/images/generate-task', body);

    // pollTask com teto limitado (~8min: maxAttempts=62, backoff 2s→4s→8s).
    // Cobre com folga o fallback lento do ByteDance (~6,5min no p95), mas fica bem
    // abaixo dos ~16min que as 120 tentativas padrão deixariam uma task travada
    // segurar o lote sequencial. Mantido pollTask (não listenToTask) para preservar o
    // tratamento de janela de manutenção e não depender do WebSocket/Echo na geração.
    const completedTask = await taskPollingService.pollTask((response.data as GenerationResponse).taskId, 2000, 62);

    if (completedTask.images && completedTask.images.length > 0) {
        return {
            id: completedTask.images[0].id,
            url: completedTask.images[0].url,
            // Modelo efetivo + task: já vinham na resposta do polling e eram
            // descartados. Alimentam a tag de atribuição interna.
            model: completedTask.images[0].metadata?.model ?? null,
            taskId: completedTask.id
        };
    }
    return null;
};

// Video Generation
const generateVideo = async (
    imageFile: File | number,
    prompt: string,
    aspectRatio: '16:9' | '9:16',
    resolution: '720p' | '1080p'
): Promise<GenerationResponse> => {
    // Ensure campaign exists
    const campaignId = await ensureCampaign();

    let imageId: number;

    if (imageFile instanceof File) {
        // 1. Upload Image
        const formData = new FormData();
        formData.append('image_file', imageFile);
        formData.append('status', 'uploaded');
        formData.append('campaign_id', campaignId.toString());

        const uploadResponse = await api.post('/images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        imageId = (uploadResponse.data as any).data.id;
    } else {
        // Use existing image ID
        imageId = typeof imageFile === 'string' ? parseInt(imageFile) : imageFile;
    }

    // 2. Create Video Task
    const response = await api.post('/videos/generate-task', {
        name: 'Video Generation - ' + new Date().toLocaleTimeString(),
        prompt: prompt,
        source_image_ids: [imageId],
        campaign_id: campaignId,
        aspect_ratio: aspectRatio,
        resolution: resolution
    });
    return response.data as GenerationResponse;
};

// Home Decor Scene Generation
const generateHomeDecorScene = async (
    productFiles: File[],
    environment: { type: 'prompt', value: string } | { type: 'image', file: File },
    aspectRatio: string,
    stylePrompt?: string,
    brandbookId?: number,
    brandStyleSource?: 'specific' | 'auto' | 'none',
    tagIds?: number[],
    outputFormat: 'png' | 'jpeg' = 'png'
): Promise<{ url: string; id: number } | null> => {
    // Ensure we have a Home Decor campaign
    const campaignId = await ensureHomeDecorCampaign();

    // Upload products
    const productIds = await Promise.all(productFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('image_file', file);
        formData.append('status', 'uploaded');
        formData.append('campaign_id', campaignId.toString());

        const response = await api.post('/images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return (response.data as any).data.id;
    }));

    const sourceImageIds = [...productIds];
    let shootingPrompt = 'Products in scene. ';

    if (environment.type === 'image') {
        // Upload environment image
        const envFormData = new FormData();
        envFormData.append('image_file', environment.file);
        envFormData.append('status', 'uploaded');
        envFormData.append('campaign_id', campaignId.toString());
        const envUpload = await api.post('/images', envFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        const envImageId = (envUpload.data as any).data.id;
        sourceImageIds.push(envImageId);
        shootingPrompt += "Place products in the provided environment image. ";
    } else {
        shootingPrompt += `Environment: ${environment.value}. `;
    }

    if (stylePrompt) {
        shootingPrompt += `Style: ${stylePrompt}. `;
    }

    const response = await api.post('/images/generate-task', {
        name: 'Home Decor - ' + new Date().toLocaleTimeString(),
        prompt: '', // Ignored for home-decor, but required by API validation? No, nullable.
        task_parameters: {
            shooting_prompt: shootingPrompt
        },
        n: 1,
        size: aspectRatio,
        campaign_id: campaignId,
        source_image_ids: sourceImageIds,
        brandbook_id: brandbookId,
        brand_style_source: brandStyleSource || 'none',
        tag_ids: tagIds,
        output_format: outputFormat
    });

    const completedTask = await taskPollingService.pollTask((response.data as GenerationResponse).taskId);

    if (completedTask.images && completedTask.images.length > 0) {
        return {
            url: completedTask.images[0].url,
            id: completedTask.images[0].id
        };
    }
    return null;
};

// Edit Image API call (using backend)
const editImage = async (
    image: File | number,
    prompt: string,
    campaignId: number | undefined = undefined,
    referenceImages?: (File | number)[], // Accept IDs or Files
    aspectRatio?: string, // Aspect ratio (e.g., '1:1', '16:9', '4:3')
    imageSize?: '1K' | '2K' | '4K', // Resolution for Gemini
    source?: string, // Origin marker (e.g., 'moodboard') → routes provider chain on the backend
    outputFormat: 'png' | 'jpeg' = 'png',
    // Modelo fixado pela equipe interna (@snapfy.ai). Quando presente, o backend
    // substitui a cadeia de 'edit' por uma de UMA entrada com este modelo — o
    // `provider` do payload abaixo deixa de decidir.
    modelVersion?: string
): Promise<{ url: string; id: number } | null> => {
    const humps = (await import('humps')).default;
    // 1. Ensure campaign exists (use provided or create/get from storage)
    const effectiveCampaignId = campaignId || await ensureCampaign();

    let baseImageId: number;

    if (image instanceof File) {
        // 2. Upload base image to S3
        const formData = new FormData();
        formData.append('image_file', image);
        formData.append('status', 'uploaded');
        formData.append('campaign_id', effectiveCampaignId.toString());

        const uploadResponse = await api.post('/images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        baseImageId = (uploadResponse.data as any).data.id;
    } else {
        // Use existing image ID
        baseImageId = typeof image === 'string' ? parseInt(image) : image;
    }

    // 3. Upload reference images to S3 (if any) or use existing IDs
    const referenceImageIds: number[] = [];
    if (referenceImages && referenceImages.length > 0) {
        for (const refImage of referenceImages) {
            if (typeof refImage === 'number') {
                // Already a backend ID, use it directly
                referenceImageIds.push(refImage);
            } else {
                // File object, upload it
                const refFormData = new FormData();
                refFormData.append('image_file', refImage);
                refFormData.append('status', 'uploaded');
                refFormData.append('campaign_id', effectiveCampaignId.toString());

                const refUploadResponse = await api.post('/images', refFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                referenceImageIds.push((refUploadResponse.data as any).data.id);
            }
        }
    }

    // 4. Create a task for editing (required by the edit endpoint structure)
    const taskPayload: any = {
        name: 'Image Edit - ' + new Date().toLocaleTimeString(),
        type: 'image_edit',
        status: 'pending'
    };
    if (source) {
        taskPayload.composition = { source_app: source };
    }
    const taskResponse = await api.post(`/campaigns/${effectiveCampaignId}/tasks`, taskPayload);

    const taskId = (taskResponse.data as any).data.id;

    // 5. Submit edit request
    const editPayload: any = {
        edit_prompt: referenceImageIds.length > 0
            ? `[IMAGE EDIT TASK]
You are modifying the FIRST image provided (the original image to be edited).

USER REQUEST: ${prompt}

EDITING GUIDELINES:
- The first image is the subject to be modified - apply changes to this image only
- Additional ${referenceImageIds.length} reference image(s) are provided as visual guides for style, color, or details
- Maintain the original composition, pose, lighting, and overall scene
- Apply the requested changes naturally and realistically
- Preserve all aspects not mentioned in the user request`
            : `[IMAGE EDIT TASK]
You are modifying the provided image.

USER REQUEST: ${prompt}

EDITING GUIDELINES:
- Apply the requested changes naturally and realistically
- Maintain the original composition, lighting, and overall quality
- Preserve all aspects not mentioned in the user request`,
        provider: 'gemini', // Use Gemini exclusively
        size: aspectRatio || '1:1', // Pass aspect ratio directly (backend converts to dimensions)
        output_size: aspectRatio || '1:1', // Alternative field for aspect ratio
        image_size: imageSize || '1K', // Resolution: 1K, 2K, or 4K
        output_format: outputFormat, // PNG (default) or JPEG — backend converts on save
        quality: 'high',
        preserve_style: true,
        notify_on_completion: false
    };

    if (referenceImageIds.length > 0) {
        editPayload.reference_image_ids = referenceImageIds;
    }
    if (modelVersion) editPayload.model_version = modelVersion;

    const editResponse = await api.post(
        `/tasks/${taskId}/images/${baseImageId}/edit`,
        editPayload
    );

    console.log('Echo availability check:', {
        hasWindow: typeof window !== 'undefined',
        hasEcho: !!window.Echo,
        echoConnector: window.Echo?.connector?.name || 'none'
    });

    // 6. Listen for completion via Reverb (real-time)
    return new Promise<{ url: string; id: number }>((resolve, reject) => {
        const channel = `task.${taskId}`;
        let hasResolved = false;

        // Timeout after 8 minutes (4K images can take 5-6 minutes)
        const timeout = setTimeout(() => {
            if (!hasResolved) {
                hasResolved = true;
                window.Echo?.leave(channel);
                console.error('Editor: Edit timeout (8 minutes)');
                reject(new Error('Image edit timeout after 8 minutes'));
            }
        }, 480000);

        // Listen for ImageEditCompleted event
        // Backend broadcasts as 'image.edit.completed.with.comparison'
        window.Echo?.private(channel)
            .listen('.image.edit.completed.with.comparison', (e: any) => {
                if (hasResolved) return;
                hasResolved = true;

                // Camelize event data
                const event = humps.camelizeKeys(e) as any;

                console.log('Editor: Received image.edit.completed event:', event);
                clearTimeout(timeout);
                window.Echo?.leave(channel);

                // Backend sends: event.data.edited_image {id, url}
                // Humps converts edited_image -> editedImage
                const editedImageUrl = event?.data?.editedImage?.url || event?.editedImage?.url;
                const editedImageId = event?.data?.editedImage?.id || event?.editedImage?.id;

                if (editedImageUrl && editedImageId) {
                    resolve({ url: editedImageUrl, id: editedImageId });
                } else {
                    console.error('Editor: No edited image URL/ID in event:', event);
                    reject(new Error('No edited image in event payload'));
                }
            })
            .error((error: any) => {
                if (hasResolved) return;
                hasResolved = true;

                console.error('Editor: Echo channel error:', error);
                clearTimeout(timeout);
                window.Echo?.leave(channel);
                reject(new Error(`Echo error: ${error.message || 'Unknown error'}`));
            });

        // Fallback: If Echo is not available, fall back to polling
        if (!window.Echo) {
            console.warn('Editor: Echo not available, falling back to polling');
            clearTimeout(timeout);

            taskPollingService.pollTask(taskId)
                .then((completedTask) => {
                    if (hasResolved) return;
                    hasResolved = true;

                    if (completedTask.images && completedTask.images.length > 0) {
                        const img = completedTask.images[0];
                        resolve({ url: img.url, id: img.id });
                    } else {
                        reject(new Error('Failed to edit image (no images in completed task)'));
                    }
                })
                .catch((error) => {
                    if (hasResolved) return;
                    hasResolved = true;
                    reject(error);
                });
        }
    });
};

// Advanced Video Generation (supports all 4 modes: text, image, start-end, reference)
type VideoMode = 'text' | 'image' | 'start-end' | 'reference';

const generateVideoAdvanced = async (
    mode: VideoMode,
    prompt: string,
    aspectRatio: '16:9' | '9:16',
    resolution: '720p' | '1080p',
    images?: {
        start?: File | null;
        end?: File | null;
        references?: File[];
    },
    options?: {
        negativePrompt?: string;
        durationSeconds?: number;
        seed?: number | null;
        personGeneration?: 'allow_all' | 'allow_adult' | 'dont_allow';
    }
): Promise<GenerationResponse> => {
    const campaignId = await ensureCampaign();
    const sourceImageIds: number[] = [];

    // Upload images based on mode
    const uploadImage = async (file: File): Promise<number> => {
        const formData = new FormData();
        formData.append('image_file', file);
        formData.append('status', 'uploaded');
        formData.append('campaign_id', campaignId.toString());

        const uploadResponse = await api.post('/images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return (uploadResponse.data as any).data.id;
    };

    if (mode === 'image' && images?.start) {
        sourceImageIds.push(await uploadImage(images.start));
    } else if (mode === 'start-end') {
        if (images?.start) sourceImageIds.push(await uploadImage(images.start));
        if (images?.end) sourceImageIds.push(await uploadImage(images.end));
    } else if (mode === 'reference' && images?.references) {
        for (const ref of images.references) {
            sourceImageIds.push(await uploadImage(ref));
        }
    }

    const body: Record<string, unknown> = {
        name: 'Video Generation - ' + new Date().toLocaleTimeString(),
        prompt: prompt,
        campaign_id: campaignId,
        aspect_ratio: aspectRatio,
        resolution: resolution,
        mode: mode,
    };

    if (sourceImageIds.length > 0) {
        body.source_image_ids = sourceImageIds;
    }

    // Optional generation controls — the API (GenerateVideoTaskRequest) already validates these.
    if (options?.negativePrompt?.trim()) body.negative_prompt = options.negativePrompt.trim();
    if (options?.durationSeconds) body.duration_seconds = options.durationSeconds;
    if (options?.seed !== undefined && options.seed !== null) body.seed = options.seed;
    if (options?.personGeneration) body.person_generation = options.personGeneration;

    const response = await api.post('/videos/generate-task', body);
    return response.data as GenerationResponse;
};

// Upload a single source image (e.g. a base garment for the workflow ImageUpload
// node) and return its permanent backend id so generation can reuse it without a
// fresh upload. Mirrors the inline upload used by generateLook/generateVideoAdvanced.
const uploadSourceImage = async (file: File): Promise<{ id: number; url: string }> => {
    const campaignId = await ensureCampaign();
    const formData = new FormData();
    formData.append('image_file', file);
    formData.append('status', 'uploaded');
    formData.append('campaign_id', campaignId.toString());

    const response = await api.post('/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    const data = (response.data as any).data;
    return { id: data.id, url: data.url ?? data.image_url ?? data.s3_url ?? '' };
};

// Reference-to-video from one or more ALREADY-UPLOADED images (the workflow uploads its
// source images up front and holds their backend ids). Mirrors generateVideoAdvanced
// in 'reference' mode but skips the File upload step — used by the workflow video node
// to feed every connected image (e.g. a generated look + a garment) to Veo at once.
const generateVideoFromReferenceIds = async (
    imageIds: number[],
    prompt: string,
    aspectRatio: '16:9' | '9:16',
    resolution: '720p' | '1080p'
): Promise<GenerationResponse> => {
    const campaignId = await ensureCampaign();
    const body: Record<string, unknown> = {
        name: 'Video Generation - ' + new Date().toLocaleTimeString(),
        prompt,
        campaign_id: campaignId,
        aspect_ratio: aspectRatio,
        resolution,
        mode: 'reference',
        source_image_ids: imageIds,
    };
    const response = await api.post('/videos/generate-task', body);
    return response.data as GenerationResponse;
};

// Extend an existing video by N seconds (Veo only — Seedance has no native extend).
// Backend: POST /videos/{id}/extend (body: name, prompt?, extra_seconds 1-7) → taskId.
const extendVideo = async (
    videoId: number,
    extraSeconds: number,
    prompt?: string,
    name?: string,
): Promise<GenerationResponse> => {
    const body: Record<string, unknown> = {
        name: name?.trim() || `Extended video - ${new Date().toLocaleTimeString()}`,
        extra_seconds: extraSeconds,
    };
    if (prompt?.trim()) body.prompt = prompt.trim();

    const response = await api.post(`/videos/${videoId}/extend`, body);
    return response.data as GenerationResponse;
};

// ---- Audio narration (ElevenLabs text-to-speech) ----

export interface AudioVoiceOption { voiceId: string; name: string; gender?: string }
export interface AudioModelOption { modelId: string; name: string }
export interface AudioVoiceSettingsDefaults {
    stability: number;
    similarityBoost: number;
    style: number;
    useSpeakerBoost: boolean;
    speed: number;
}
export interface AudioOptions {
    enabled: boolean;
    voices: AudioVoiceOption[];
    models: AudioModelOption[];
    outputFormats: string[];
    defaultVoiceId?: string;
    defaultModel?: string;
    voiceSettingsDefaults?: AudioVoiceSettingsDefaults;
}

export interface AudioNarrationParams {
    voiceId: string;
    modelId?: string;
    outputFormat?: string;
    stability?: number;
    similarityBoost?: number;
    style?: number;
    speed?: number;
    useSpeakerBoost?: boolean;
}

// Voices/models/defaults the narration node offers. Served by the API from a curated
// catalog (or the live ElevenLabs account when the key can read it). Plain JSON payload
// (not a Resource), so it's read directly off response.data.
const getAudioOptions = async (): Promise<AudioOptions> => {
    const response = await api.get('/audio/options');
    return response.data as AudioOptions;
};

// Turn text into an MP3 narration. The backend (GenerateAudioTaskRequest) accepts the
// voice settings as flat fields and assembles ElevenLabs' voice_settings server-side.
const generateAudioNarration = async (
    text: string,
    params: AudioNarrationParams,
): Promise<GenerationResponse> => {
    const campaignId = await ensureCampaign();
    const body: Record<string, unknown> = {
        name: 'Audio Narration - ' + new Date().toLocaleTimeString(),
        campaign_id: campaignId,
        text,
        voice_id: params.voiceId,
    };
    if (params.modelId) body.model_id = params.modelId;
    if (params.outputFormat) body.output_format = params.outputFormat;
    if (params.stability !== undefined) body.stability = params.stability;
    if (params.similarityBoost !== undefined) body.similarity_boost = params.similarityBoost;
    if (params.style !== undefined) body.style = params.style;
    if (params.speed !== undefined) body.speed = params.speed;
    if (params.useSpeakerBoost !== undefined) body.use_speaker_boost = params.useSpeakerBoost;

    const response = await api.post('/audio/generate-task', body);
    return response.data as GenerationResponse;
};

// Merge an already-generated video with an already-generated narration into a single
// MP4 with voiceover (server-side ffmpeg). Takes backend ids (not signed URLs, which
// expire). Backend: POST /videos/merge-task → taskId; the merged Video lands in task.videos.
const mergeNarratedVideo = async (
    videoId: number,
    audioId: number,
): Promise<GenerationResponse> => {
    const campaignId = await ensureCampaign();
    const body: Record<string, unknown> = {
        name: 'Narrated Video - ' + new Date().toLocaleTimeString(),
        campaign_id: campaignId,
        video_id: videoId,
        audio_id: audioId,
    };
    const response = await api.post('/videos/merge-task', body);
    return response.data as GenerationResponse;
};

// Kling Omni (kling-video-o1) video task. One endpoint drives all 7 generation modes;
// the backend (GenerateVideoTaskRequest + KlingVideoService) maps `generation_mode` +
// the supplied fields onto the omni-video payload. Used by the workflow video node.
const generateOmniVideo = async (params: {
    generationMode: string;
    prompt: string;
    aspectRatio: '16:9' | '9:16' | '1:1';
    resolution: '720p' | '1080p' | '4k';
    sound?: boolean;
    durationSeconds?: number;
    sourceImageIds?: number[];
    referenceVideoId?: number;
    shots?: { prompt: string; duration: number }[];
}): Promise<GenerationResponse> => {
    const campaignId = await ensureCampaign();
    const body: Record<string, unknown> = {
        name: 'Video Generation - ' + new Date().toLocaleTimeString(),
        campaign_id: campaignId,
        generation_mode: params.generationMode,
        prompt: params.prompt,
        aspect_ratio: params.aspectRatio,
        resolution: params.resolution,
        sound: Boolean(params.sound),
    };
    if (params.durationSeconds) body.duration_seconds = params.durationSeconds;
    if (params.sourceImageIds && params.sourceImageIds.length > 0) body.source_image_ids = params.sourceImageIds;
    if (params.referenceVideoId) body.reference_video_id = params.referenceVideoId;
    if (params.shots && params.shots.length > 0) body.shots = params.shots;

    const response = await api.post('/videos/generate-task', body);
    return response.data as GenerationResponse;
};

/**
 * Canvas do Product Studio: vídeo no Seedance 2.5 (source_app = canvas_video), de 4 a
 * 15 s. `start_end_frames` usa as imagens como primeiro/último quadro (proporção da
 * imagem); `image_element_reference`, como referência (@Image1…); sem imagem, texto puro.
 * Com o Seedance 2.5 desligado, o backend cai na cadeia Kling → Veo → Seedance 1.5.
 */
const generateCanvasVideo = async (params: {
    generationMode: 'text_to_video' | 'start_end_frames' | 'image_element_reference';
    prompt: string;
    aspectRatio: string;
    resolution: '720p' | '1080p';
    durationSeconds: number;
    generateAudio: boolean;
    sourceImageIds?: number[];
}): Promise<GenerationResponse> => {
    const campaignId = await ensureCampaign();
    const body: Record<string, unknown> = {
        name: 'Canvas - vídeo ' + new Date().toLocaleTimeString(),
        campaign_id: campaignId,
        source_app: 'canvas_video',
        generation_mode: params.generationMode,
        prompt: params.prompt,
        aspect_ratio: params.aspectRatio,
        resolution: params.resolution,
        duration_seconds: params.durationSeconds,
        generate_audio: params.generateAudio,
        sound: params.generateAudio,
    };
    if (params.sourceImageIds && params.sourceImageIds.length > 0) body.source_image_ids = params.sourceImageIds;
    const response = await api.post('/videos/generate-task', body);
    return response.data as GenerationResponse;
};

/**
 * Marketing Studio render.
 *
 * The catalogue template decides the framing, the length and — for video —
 * which provider the backend routes to, so the client only names the slug.
 * The reference clip itself is never handled here: the backend resolves it
 * from the template's own storage key, or from `sourceVideoId` when the
 * customer is iterating on a clip they already own.
 */
const generateMarketingAd = async (params: {
    prompt: string;
    templateSlug?: string;
    kind: 'image' | 'video';
    aspectRatio: string;
    resolution?: string | null;
    durationSeconds?: number | null;
    /** Fotos de referência do produto — todas vão como source_image_ids. */
    productFiles?: File[] | null;
    /** Nomes alinhados a productFiles ("@nome" no prompt aponta para a imagem). */
    imageLabels?: string[] | null;
    /** Set when editing a previous result instead of starting from the template. */
    sourceVideoId?: number | null;
    // ---- vídeo (Seedance) ----
    /** Som nativo do Seedance (open/close no playground). */
    generateAudio?: boolean | null;
    /** Contêiner do arquivo final. */
    outputFormat?: 'mp4' | 'mov' | null;
    // ---- imagem (Seedream 5.0 Pro) ----
    imageSize?: '1K' | '2K' | '4K' | null;
    imageFormat?: 'png' | 'jpeg' | null;
    /** 'marketing' (padrão, catálogo) ou 'marketing_layers' (Seedream Pro, prompt cru). */
    sourceApp?: 'marketing' | 'marketing_layers';
}): Promise<GenerationResponse> => {
    const campaignId = await ensureCampaign();

    // Uploads em sequência para preservar a ordem escolhida pelo cliente.
    const sourceImageIds: number[] = [];
    for (const file of params.productFiles ?? []) {
        const formData = new FormData();
        formData.append('image_file', file);
        formData.append('status', 'uploaded');
        formData.append('campaign_id', campaignId.toString());
        const uploadResponse = await api.post('/images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        sourceImageIds.push((uploadResponse.data as any).data.id);
    }

    if (params.kind === 'video') {
        const body: Record<string, unknown> = {
            name: 'Marketing Studio - ' + new Date().toLocaleTimeString(),
            prompt: params.prompt,
            campaign_id: campaignId,
            aspect_ratio: params.aspectRatio,
            resolution: params.resolution ?? '720p',
            mode: 'generate',
            source_app: 'marketing',
        };
        if (params.templateSlug) body.template_slug = params.templateSlug;
        // -1 = tempo inteligente (o modelo escolhe); 0/undefined = deixa o template decidir.
        if (params.durationSeconds) body.duration_seconds = params.durationSeconds;
        if (params.sourceVideoId) body.source_video_id = params.sourceVideoId;
        if (typeof params.generateAudio === 'boolean') body.generate_audio = params.generateAudio;
        if (params.outputFormat) body.output_format = params.outputFormat;
        if (sourceImageIds.length > 0) body.source_image_ids = sourceImageIds;

        const response = await api.post('/videos/generate-task', body);
        return response.data as GenerationResponse;
    }

    const body: Record<string, unknown> = {
        name: 'Marketing Studio - ' + new Date().toLocaleTimeString(),
        prompt: params.prompt,
        n: 1,
        size: params.aspectRatio,
        quality: 'high',
        draft: false,
        campaign_id: campaignId,
        source_app: params.sourceApp ?? 'marketing',
    };
    if (params.templateSlug) body.template_slug = params.templateSlug;
    if (params.imageSize) body.image_size = params.imageSize;
    if (params.imageFormat) body.output_format = params.imageFormat;
    if (sourceImageIds.length > 0) body.source_image_ids = sourceImageIds;
    const labels = (params.imageLabels ?? []).slice(0, sourceImageIds.length).map((l) => (l ?? '').trim());
    if (labels.some((l) => l !== '')) body.source_image_labels = sourceImageIds.map((_, i) => labels[i] ?? '');

    const response = await api.post('/images/generate-task', body);
    return response.data as GenerationResponse;
};

/**
 * Canvas do Marketing Studio: reescreve um vídeo já gerado (do próprio usuário) só com
 * prompt — Seedance Omni em modo edit, enquadramento e duração vêm do vídeo de origem.
 * As imagens selecionadas no board vão como referência (@Image1…).
 */
const editMarketingVideo = async (params: {
    sourceVideoId: number;
    prompt: string;
    referenceFiles?: File[] | null;
    generateAudio?: boolean;
    outputFormat?: 'mp4' | 'mov';
}): Promise<GenerationResponse> => {
    const campaignId = await ensureCampaign();
    const sourceImageIds: number[] = [];
    for (const file of params.referenceFiles ?? []) {
        const formData = new FormData();
        formData.append('image_file', file);
        formData.append('status', 'uploaded');
        formData.append('campaign_id', campaignId.toString());
        const uploadResponse = await api.post('/images', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        sourceImageIds.push((uploadResponse.data as any).data.id);
    }
    const body: Record<string, unknown> = {
        name: 'Marketing Studio - edição de vídeo ' + new Date().toLocaleTimeString(),
        prompt: params.prompt,
        campaign_id: campaignId,
        mode: 'generate',
        source_app: 'marketing_layers',
        source_video_id: params.sourceVideoId,
        generate_audio: params.generateAudio ?? false,
        output_format: params.outputFormat ?? 'mp4',
    };
    if (sourceImageIds.length > 0) body.source_image_ids = sourceImageIds;
    const response = await api.post('/videos/generate-task', body);
    return response.data as GenerationResponse;
};

export const generationService = {
    generateFromMoodboard,
    generateMarketingAd,
    editMarketingVideo,
    generateClothing,
    generateImage,
    generateLook,
    generateNewPose,
    generateVideo,
    generateVideoAdvanced,
    generateVideoFromReferenceIds,
    generateOmniVideo,
    generateCanvasVideo,
    extendVideo,
    generateHomeDecorScene,
    editImage,
    uploadSourceImage,
    getAudioOptions,
    generateAudioNarration,
    mergeNarratedVideo,
};
